
import { Box, DialogContent, Grid } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { StepConfig } from '../../../../redux/views/viewsTypes'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { DialogFooter } from '../../../../components/features/Dialog/BasicFooterDialog'
import { Form, FormikBag, FormikProps, withFormik } from 'formik'
import { monthAsArray } from '../../../../helpers/months'
import { formateDateToString } from '../../../../components/features/utils/formateDateToString'
import { differenceInMonths } from '../../../../components/features/GlobalPages/Private/Period'
import { updateFileData } from '../../../../redux/imports/importsSlice'
import { date, object, ref, string } from 'yup'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { PrimaryText } from '../../../../components/ui/Text'
import { Select, MenuItem } from '@mui/material'
import { EnvName } from '../../../../redux/user/userTypes'
import ErrorMessage from '../../../../components/ui/ErrorMessage'
import { CalendarDatePicker } from '../../../../components/features/filters/FilterCalendar'
import { DataType } from './Import'

const distributorsMapping = {
    [EnvName.FRANCE_FRAIS]: 'FFR',
    [EnvName.TRANSGOURMET]: 'TGM',
    [EnvName.C10]: 'C10',
    [EnvName.CERCLEVERT]: 'CVR',
    [EnvName.SODEXO]: 'SXO',
    [EnvName.GBH]: 'GBH',
    [EnvName.METRO]: 'METRO',
    [EnvName.PASSIONFROID]: 'PF',
    [EnvName.DISTRIBOISSONS]: 'DISTRIBOISSONS',
}

interface OwnProps {
    config: StepConfig
    step: number
    files: any
    envNames: string[]
    companyName: string
    dataType: DataType
}

interface FormValues {
    startDate: Date
    endDate: Date
    distributor: string
}

interface FormProps {
    initialData: FormValues
    config: StepConfig
    step: number
    onSubmit: (values: FormValues) => void
    files: any
    envNames: string[]
    companyName: string

}

const PureViewFilterStepForm = ({
    setFieldValue,
    ...props
}: FormikProps<FormValues> & FormProps) => {
    const columns: GridColDef[] = [
        { field: 'fileName', headerName: 'Fichier', flex: 2 },
        {
            field: 'distributor',
            headerName: 'Distributeur',
            type: 'string',
            renderCell: () => {
                const [value, setValue] = useState(props.values.distributor);
                const handleChange = (event: any) => {
                    const newValue = event.target.value as string;
                    setValue(newValue);
                    setFieldValue('distributor', newValue);
                };

                return (
                    <Select
                        value={value}
                        onChange={handleChange}
                        fullWidth
                        name='distributor'
                    >
                        {props.envNames.map((el) =>
                            <MenuItem value={el}>{el}</MenuItem>)
                        }
                    </Select>
                );
            },
            flex: 2,
        },
        {
            field: 'startDate',
            headerName: 'Début',
            renderCell: () => {
                return (
                    <Grid>

                        <LocalizationProvider
                            adapterLocale="fr"
                            dateAdapter={AdapterDayjs}
                        >
                            <CalendarDatePicker
                                min={new Date("2022-01")}
                                max={new Date()}
                                setFieldValue={setFieldValue}
                                name="startDate"
                                value={props.values.startDate}
                            />
                        </LocalizationProvider>
                        {
                            props.errors.startDate && (
                                <Grid item xs={12}>
                                    <ErrorMessage
                                        msg={props.errors.startDate as string}
                                        style={{ width: '100%', whiteSpace: 'normal', wordBreak: 'break-word' }}
                                    />
                                </Grid>
                            )
                        }
                    </Grid>
                )
            },
            flex: 2,
        },
        {
            field: 'endDate',
            headerName: 'Fin',
            renderCell: () => {
                return (
                    <Grid>
                        <LocalizationProvider
                            adapterLocale="fr"
                            dateAdapter={AdapterDayjs}
                        >
                            <CalendarDatePicker
                                min={new Date("2022-01")}
                                max={new Date()}
                                setFieldValue={setFieldValue}
                                name="endDate"
                                value={props.values.endDate}
                            />
                        </LocalizationProvider>
                        {props.errors.endDate && (
                            <Grid item xs={12}>
                                <ErrorMessage
                                    msg={props.errors.endDate as string}
                                    style={{ width: '100%', whiteSpace: 'normal', wordBreak: 'break-word' }}
                                />
                            </Grid>
                        )}
                    </Grid>
                )
            },
            flex: 2,
        }
    ]

    return (
        <Form style={{ width: '100%' }}>
            <DialogContent>
                <Grid paddingBottom="10vh" >
                    <PrimaryText>
                        Vous venez de déposer 1 fichier. Complétez les champs ci-dessous ✏️ :
                    </PrimaryText>
                    <LocalizationProvider
                        adapterLocale="fr"
                        dateAdapter={AdapterDayjs}
                    >
                        <Box
                            sx={{
                                height: 300,
                                width: "100%",
                                '& .actions': {
                                    color: 'text.secondary',
                                },
                                '& .textPrimary': {
                                    color: 'text.primary',
                                },
                            }}
                        >
                            <DataGrid
                                disableSelectionOnClick
                                rowHeight={120} columns={columns} rows={[{
                                    id: 1,
                                    fileName: props.files.name,
                                }]} />
                        </Box>
                    </LocalizationProvider>
                </Grid>
            </DialogContent>
            <DialogFooter
                rightButtonFunc={(e) => {
                    !props.errors.endDate && !props.errors.startDate &&
                        props.handleSubmit()
                }}
                rightButtonText={'Envoyer'}
                leftButtonFunc={(e) => {
                    props.config.handleClose()
                }}
                leftButtonText="Fermer"
            />
        </Form >
    )
}

const ControlledFileConfigForm = withFormik<FormProps, FormValues>({
    mapPropsToValues: (props: FormProps) => ({
        startDate: props.initialData.startDate,
        endDate: props.initialData.endDate ?? null,
        distributor: props.initialData.distributor
    }),
    enableReinitialize: true,
    validationSchema: (props: FormProps) => {
        const startMonth = props.initialData.startDate.getMonth()
        const startYear = props.initialData.startDate.getFullYear()
        const endMonth = new Date().getMonth()
        const endYear = new Date().getFullYear()

        return object().shape({
            distributor: string().required("Le distributeur est obligatoire"),
            startDate: date()
                .typeError('Entrez une date valide')
                .required('Choisissez une date de début')
                .test(
                    'start-before-end',
                    `La date de début doit être antérieure ou égale à la date de fin`,
                    function (startDate) {
                        const { endDate } = this.parent;

                        // Si pas de endDate (au chargement), on passe la validation
                        if (!endDate || !startDate) return true;

                        return startDate <= endDate;
                    }
                ),
            endDate: date()
                .nullable()
                .transform((value, originalValue) => {
                    return originalValue === '' || originalValue === null ? null : value;
                })
                .typeError('Entrez une date valide')
                .required('Choisissez une date de fin')
                .test(
                    'is-after-or-same',
                    'La date de fin doit être égale ou postérieure à la date de début',
                    function (endDate) {
                        const { startDate } = this.parent;

                        return endDate.setHours(0, 0, 0, 0) >= startDate.setHours(0, 0, 0, 0);
                    }
                )
                .test(
                    'max-today',
                    `La date de fin ne peut pas dépasser aujourd’hui`,
                    function (endDate) {
                        if (!endDate) return true;
                        return endDate <= new Date();
                    }
                )
                .test(
                    'is-within-12-months',
                    'Vous ne pouvez pas sélectionner plus de 12 mois consécutifs.',
                    function (endDate) {
                        const { startDate } = this.parent; //Différent des pages car props.date.startDate est undefined

                        if (startDate && endDate) {
                            const monthsDifference = differenceInMonths(startDate, endDate);
                            return monthsDifference <= 11;

                        }
                        return true; // Si les dates ne sont pas définies, on passe.
                    }
                ),
        })
    },
    handleSubmit: (
        values: FormValues,
        bag: FormikBag<FormProps, FormValues>
    ) => {
        bag.props.onSubmit(values)
    },
})(PureViewFilterStepForm)

export const FileConfigForm = ({ step, config, files, envNames, companyName, dataType }: OwnProps) => {
    const dispatch = useAppDispatch()
    const fileType = files.name.split('.')[1]
    const envName = useAppSelector((state) => state.global.env.name)

    const _updateData = useCallback(
        ({
            startDate,
            endDate,
            distributor,
        }: {

            startDate: string
            endDate: string
            distributor: string
        }) =>
            dispatch(
                updateFileData({
                    data: {
                        startDate: startDate,
                        endDate: endDate,
                        distributor: distributor,
                        fileName: `${distributorsMapping[distributor]}/${companyName.toLocaleUpperCase()}/${startDate.split('').filter((el) => el != "-").join('')}_${distributorsMapping[distributor]}_${companyName.toLocaleUpperCase()}_${dataType.toLocaleUpperCase()}.${fileType}`
                    },
                })
            ),
        [dispatch]
    )

    const today = new Date();
    const startDate = new Date(today);
    startDate.setMonth(startDate.getMonth() - 1);

    const initialDates = {
        startDate: startDate,
        endDate: null,
        distributor: envName
    };

    initialDates.startDate.setHours(0, 0, 0)

    const [shouldSubmit, setShouldSubmit] = useState(false);
    const handleSubmit = (values: FormValues) => {
        const formatedDate = formateDateToString(
            values.startDate,
            values.endDate
        );

        _updateData({
            distributor: values.distributor,
            startDate: formatedDate.start,
            endDate: formatedDate.end,
        });

        setShouldSubmit(true);
    };

    useEffect(() => {
        if (shouldSubmit) {
            config.handleSubmit();
            setShouldSubmit(false);
        }
    }, [shouldSubmit, config]);

    return (
        <ControlledFileConfigForm
            config={config}
            step={step}
            onSubmit={handleSubmit}
            initialData={initialDates}
            files={files}
            envNames={envNames}
            companyName={companyName}
        />
    )
}